/**
 * CRM effective permissions for the shell CRM sidebar.
 *
 * The shell renders the CRM nav (CRM_PANEL_SECTIONS) itself, so it must gate
 * those rows on the CRM user's fine-grained `module.*` permissions. Those live
 * in the CRM app's own Redux slice (hydrated from GET /crm/users/me/permissions)
 * — which the shell can't read across the MF boundary — and NOT in the shared
 * `@clarix/rbac` store (that one is hydrated by the projects app). So the shell
 * fetches the same endpoint directly here, purely to drive nav visibility.
 *
 * While loading, on error, or on 403 the map is null and `loaded` is false; the
 * hook itself is policy-neutral (it just reports map + loaded) and each caller
 * decides. The per-ROW gate (`crmRowVisible`) fails CLOSED — a permission-gated
 * row stays hidden until the map resolves. The rail-icon EMPTINESS check
 * (`useVisibleCrmPanelIds`) still fails OPEN (returns null → show every rail
 * icon) so the whole CRM nav never vanishes mid-load; the rows inside then gate
 * themselves fail-closed once opened.
 */

import { useQuery } from '@tanstack/react-query';
import { apiService } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';

interface MyPermissionsResponse {
  permissions: Record<string, boolean>;
}

/**
 * The CRM effective permission map (`module.*` → boolean), or null when
 * unresolved. `loaded` is true only once the fetch has succeeded, so callers
 * fail-open until then; `isError` tells a failed fetch apart from one in flight.
 */
export function useCrmPermissions(enabled = true): {
  map: Record<string, boolean> | null;
  loaded: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const hasToken = useAuthStore((s) => !!s.token);
  const { data, isSuccess, isError, refetch } = useQuery({
    queryKey: ['crm', 'my-permissions'],
    queryFn: () => apiService.get<MyPermissionsResponse>('/crm/users/me/permissions'),
    enabled: hasToken && enabled,
    staleTime: 60_000,
    gcTime: 30 * 60_000,
    retry: false,
  });
  return {
    map: data?.permissions ?? null,
    loaded: isSuccess,
    isError,
    refetch: () => void refetch(),
  };
}
